/** How close sob actuals came to plan, floored by last year's baseline: a share that ended below
 *  where it started scores nothing, however modest the plan was.
 *  - `null` when there is no positive plan to score against.
 *  - `0` if actuals are at or below baseline. The floor is checked *before* the plan, so a plan
 *    that targets a decline still scores `0` when it is met — holding a shrinking share is not an
 *    achievement here. A plan below baseline is an ordinary shape rather than a degenerate one, so
 *    this rule carries real rows rather than just an edge case.
 *  - `100` if actuals met or exceeded plan, having first cleared the baseline.
 *  - otherwise, the linear-interpolation percentage of the way from baseline to plan:
 *    `(actuals - baseline) / (plan - baseline) * 100`.
 *  Ordering the two checks this way is also what keeps the interpolation out of negative territory:
 *  past the floor, `actuals - baseline` can no longer be negative. Swapping them brings back both
 *  the negative scores and the "met a declining plan" escape hatch. */
export const getSobAchievementScore = (
  actuals: number | null,
  plan: number | null,
  baseline: number | null
): number | null => {
  const nonNullableActual = actuals ?? 0
  const nonNullablePlan = plan ?? 0
  const nonNullableBaseline = baseline ?? 0

  if (nonNullablePlan <= 0) {
    return null
  }

  if (nonNullableActual <= 0) {
    return 0
  }

  if (nonNullableActual < nonNullableBaseline) {
    return 0
  }

  if (nonNullableActual >= nonNullablePlan) {
    return 100
  }

  const denominator = nonNullablePlan - nonNullableBaseline

  if (denominator === 0) {
    return 0
  }

  return Math.round(((nonNullableActual - nonNullableBaseline) / denominator) * 100)
}
